import { faCircleCheck, faFilePdf } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	calculatePaymentMethodFee,
	collectBrowserInfo,
	formatDateSimple,
	PaymentMethodTypeEnum,
} from '@supply-mover-2-0/core';
import type {
	PublicPaymentLinkModel,
	PublicPaymentLinkPayResultModel,
	PublicPaymentLinkState,
} from '@supply-mover-2-0/shared';
import { Toast, useTheme, useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CurrencyInput } from '../components/CurrencyInput';
import {
	OpenInvoicePicker,
	type InvoicePaymentLinesModel,
} from '../components/OpenInvoicePicker';
import {
	PaymentLinkShell,
	PaymentLinkStatePanel,
	PaymentLinkSupportLine,
} from '../components/paymentLinks/PaymentLinkShell';
import { PaymentSummary } from '../components/PaymentSummary';
import {
	PortalAddPaymentMethod,
	type PortalPaymentMethodApiModel,
} from '../components/PortalAddPaymentMethod';
import { useTurnstile } from '../hooks/useTurnstile';
import { saveOrShareBlob } from '../lib/downloadFile';
import { formatCurrency } from '../lib/formatCurrency';
import {
	fetchPaymentLink,
	fetchPaymentLinkInvoicePdf,
	getPaymentLinkCaptureContext,
	PaymentLinkRequestError,
	payPaymentLink,
	toGatewayConfigResponse,
} from '../services/paymentLinkService';

const round2 = (value: number): number => Math.round(value * 100) / 100;

/**
 * PD-16674 — `/pay/:token`: pay an invoice, or a statement's open invoices,
 * from an emailed or copied link, without signing in.
 *
 * Outside PortalLayout and the signed-in route guards on purpose. Everything
 * shown comes from the link (the server decides the client, the invoices and
 * the methods offered); the page only ever takes a NEW card or bank account,
 * never one already on file. The amount defaults to the full balance and can
 * be lowered for a partial payment.
 */
export function PaymentLinkPage(): JSX.Element {
	const { t } = useTranslation();
	const { isDark } = useTheme();
	const { token = '' } = useParams<{ token: string }>();

	const [link, setLink] = useState<PublicPaymentLinkModel | null>(null);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [state, setState] = useState<PublicPaymentLinkState | null>(null);
	const [amountText, setAmountText] = useState('');
	const [paymentType, setPaymentType] = useState<PaymentMethodTypeEnum | null>(
		null,
	);
	const [saveForFuture, setSaveForFuture] = useState(false);
	const [result, setResult] = useState<PublicPaymentLinkPayResultModel | null>(
		null,
	);
	const [downloadingId, setDownloadingId] = useState<number | null>(null);
	// PD-16674 — a statement link's invoice lines, as on Make a Payment.
	const [lines, setLines] = useState<InvoicePaymentLinesModel>({});

	const load = useCallback(async (): Promise<void> => {
		setLoadError(null);
		try {
			const loaded = await fetchPaymentLink(token);
			setLink(loaded);
			setState(loaded.state);
			setAmountText(loaded.openBalance > 0 ? loaded.openBalance.toFixed(2) : '');
			// A statement link exists to pay the statement, so every invoice
			// starts ticked at its full balance; the payer can untick or lower.
			setLines(
				loaded.linkType === 'statement'
					? Object.fromEntries(
							loaded.invoices.map((invoice) => [
								invoice.invoiceId,
								{ checked: true, amount: invoice.balance.toFixed(2) },
							]),
						)
					: {},
			);
			const firstType = loaded.paymentTypes[0]?.paymentMethodType;
			setPaymentType(
				firstType === 'ACH'
					? PaymentMethodTypeEnum.ACH
					: PaymentMethodTypeEnum.CREDIT_CARD,
			);
		} catch (error) {
			if (error instanceof PaymentLinkRequestError && error.state != null) {
				setState(error.state);
				return;
			}
			setLoadError(
				error instanceof Error
					? error.message
					: 'This page could not be loaded.',
			);
		}
	}, [token]);

	useEffect(() => {
		void load();
	}, [load]);

	const turnstile = useTurnstile({
		siteKey: state === 'active' ? (link?.turnstileSiteKey ?? null) : null,
		action: link?.turnstileAction ?? 'payment-link-pay',
		theme: isDark ? 'dark' : 'light',
	});

	const openBalance = link?.openBalance ?? 0;
	const isStatementLink = link?.linkType === 'statement';

	// PD-16674 — a statement link pays the invoices the payer ticks, each for
	// the amount they choose, the same way the signed-in Make a Payment page
	// does. The server checks the same rules before charging anything.
	const chosenAllocations = useMemo(() => {
		if (link == null || link.linkType !== 'statement') {
			return [];
		}
		return link.invoices
			.map((invoice) => {
				const line = lines[invoice.invoiceId] as
					| InvoicePaymentLinesModel[number]
					| undefined;
				const lineAmount =
					line?.checked === true ? parseFloat(line.amount) : Number.NaN;
				return {
					invoiceId: invoice.invoiceId,
					amount: Number.isFinite(lineAmount) ? round2(lineAmount) : 0,
				};
			})
			.filter((line) => line.amount > 0);
	}, [link, lines]);
	const lineErrors = useMemo(() => {
		const errors: Record<number, string> = {};
		if (link == null || link.linkType !== 'statement') {
			return errors;
		}
		for (const invoice of link.invoices) {
			const line = lines[invoice.invoiceId] as
				| InvoicePaymentLinesModel[number]
				| undefined;
			if (line?.checked === true && parseFloat(line.amount) > invoice.balance + 0.005) {
				errors[invoice.invoiceId] = 'Exceeds outstanding balance';
			}
		}
		return errors;
	}, [link, lines]);

	const amount = isStatementLink
		? round2(chosenAllocations.reduce((sum, line) => sum + line.amount, 0))
		: parseFloat(amountText);
	const isAmountValid =
		Number.isFinite(amount) &&
		amount > 0 &&
		amount <= openBalance + 0.005 &&
		Object.keys(lineErrors).length === 0;

	// The fee the selected type is quoted at, applied only to the part of the
	// payment landing on invoices that do not already carry a fee — the same
	// rule the charge follows. A card fee applies to credit cards only, so the
	// summary says so; the server classifies the card and decides.
	const selectedType = useMemo(
		() =>
			link?.paymentTypes.find(
				(type) =>
					type.paymentMethodType ===
					(paymentType === PaymentMethodTypeEnum.ACH ? 'ACH' : 'Credit Card'),
			) ?? null,
		[link, paymentType],
	);
	const feeAmount = useMemo(() => {
		if (
			link == null ||
			selectedType == null ||
			selectedType.processingFee <= 0 ||
			!isAmountValid
		) {
			return 0;
		}
		const planned =
			link.linkType === 'statement'
				? chosenAllocations
				: [{ invoiceId: link.invoices[0]?.invoiceId ?? 0, amount }];
		const feeBase = planned
			.filter(
				(line) =>
					(link.invoices.find((invoice) => invoice.invoiceId === line.invoiceId)
						?.carriedProcessingFee ?? 0) <= 0.005,
			)
			.reduce((sum, line) => sum + line.amount, 0);
		return feeBase > 0.005
			? calculatePaymentMethodFee(round2(feeBase), selectedType)
			: 0;
	}, [link, selectedType, isAmountValid, amount, chosenAllocations]);
	const total = isAmountValid ? round2(amount + feeAmount) : 0;
	const isCardFee =
		paymentType !== PaymentMethodTypeEnum.ACH && feeAmount > 0.005;

	// What the submit reads at the moment Pay is pressed. Held in a ref so the
	// form's calls stay the same object while the customer edits the amount:
	// a new object would make the card form fetch its configuration again and
	// rebuild the hosted card fields mid-entry.
	const invoiceAllocations = isStatementLink ? chosenAllocations : null;
	const latest = useRef({
		amount,
		invoiceAllocations,
		isAmountValid,
		isStatementLink,
		openBalance,
		saveForFuture,
		turnstile,
	});
	latest.current = {
		amount,
		invoiceAllocations,
		isAmountValid,
		isStatementLink,
		openBalance,
		saveForFuture,
		turnstile,
	};

	const api = useMemo<PortalPaymentMethodApiModel | null>(() => {
		if (link == null) {
			return null;
		}
		return {
			getGatewayConfig: async () => toGatewayConfigResponse(link),
			getCyberSourceCaptureContext: async (type) =>
				getPaymentLinkCaptureContext(token, type),
			submit: async (dto) => {
				const current = latest.current;
				if (!current.isAmountValid) {
					throw new Error(
						current.isStatementLink
							? t('Tick at least one invoice and check the amounts.')
							: `${t('Enter an amount greater than zero and no more than')} ${t(formatCurrency(current.openBalance) ?? '')}.`,
					);
				}
				const turnstileToken = await current.turnstile.getToken();
				if (turnstileToken == null) {
					throw new Error(t('Please complete the security check and try again.'));
				}
				try {
					const paid = await payPaymentLink(token, {
						amount: round2(current.amount),
						...(current.invoiceAllocations != null
							? { invoiceAllocations: current.invoiceAllocations }
							: {}),
						paymentMethod: dto,
						saveForFuture: current.saveForFuture,
						turnstileToken,
						browserInfo: collectBrowserInfo() ?? undefined,
					});
					setResult(paid);
					return true;
				} catch (error) {
					if (
						error instanceof PaymentLinkRequestError &&
						error.state != null &&
						error.state !== 'active'
					) {
						Toast.error(t(error.message));
						setState(error.state);
						return false;
					}
					throw new Error(
						t(error instanceof Error ? error.message : 'Payment failed.'),
					);
				} finally {
					// Turnstile tokens are single-use.
					current.turnstile.reset();
				}
			},
		};
	}, [link, token, t]);

	// The type the card form opens on: the first the link offers. Fixed for
	// the page's life — the form owns the selection after that.
	const initialPaymentType = useMemo(
		() =>
			link?.paymentTypes[0]?.paymentMethodType === 'ACH'
				? PaymentMethodTypeEnum.ACH
				: PaymentMethodTypeEnum.CREDIT_CARD,
		[link],
	);

	const downloadInvoice = async (
		invoiceId: number,
		number: string,
	): Promise<void> => {
		setDownloadingId(invoiceId);
		try {
			const blob = await fetchPaymentLinkInvoicePdf(token, invoiceId);
			await saveOrShareBlob(blob, `Invoice-${number}.pdf`);
		} catch {
			Toast.error(t('The invoice could not be downloaded. Please try again.'));
		} finally {
			setDownloadingId(null);
		}
	};

	if (result != null) {
		return (
			<PaymentLinkShell>
				<div className="bg-brand-surface border border-brand-border rounded-xl p-6 sm:p-8 text-center space-y-3">
					<FontAwesomeIcon icon={faCircleCheck} className="text-4xl text-success" />
					<h1 className="text-2xl font-semibold text-default">
						{result.transactionStatus === 'Succeeded'
							? t('Payment received. Thank you!')
							: t('Payment submitted. Thank you!')}
					</h1>
					<p className="text-sm text-brand-muted">
						{`${t(formatCurrency(result.amountCharged) ?? '')} ${t('was charged to your')} ${t(result.paymentMethodLabel)}.`}
					</p>
					{result.transactionStatus !== 'Succeeded' ? (
						<p className="text-sm text-brand-muted">
							{t(
								'Bank payments take a few days to clear. The balance updates once it does.',
							)}
						</p>
					) : null}
					{result.receiptEmailed ? (
						<p className="text-sm text-brand-muted">
							{t('A receipt has been emailed to the address on your account.')}
						</p>
					) : null}
					{result.appliedTo.length > 1 ? (
						<ul className="text-sm text-default space-y-1">
							{result.appliedTo.map((line) => (
								<li key={line.invoiceId}>
									{`${t('Invoice')} ${t('#')}${t(line.number)}: ${t(formatCurrency(line.amount) ?? '')}`}
								</li>
							))}
						</ul>
					) : null}
					{result.remainingBalance > 0.005 ? (
						<p className="text-sm text-default">
							{`${t('Remaining balance:')} ${t(formatCurrency(result.remainingBalance) ?? '')}`}
						</p>
					) : (
						// Only what this link covers: the customer may still owe
						// other invoices, so never "nothing is due".
						<p className="text-sm text-default">
							{link?.linkType === 'statement'
								? t('These invoices are fully paid.')
								: t('This invoice is fully paid.')}
						</p>
					)}
					{result.paymentMethodSaved ? (
						<p className="text-sm text-brand-muted">
							{t('Your payment method was saved for future payments.')}
						</p>
					) : null}
					<div className="pt-2 flex justify-center">
						<PaymentLinkSupportLine />
					</div>
				</div>
			</PaymentLinkShell>
		);
	}

	if (loadError != null) {
		return (
			<PaymentLinkShell>
				<div className="bg-brand-surface border border-brand-border rounded-xl p-6 text-center space-y-4">
					<p className="text-sm text-danger">{t(loadError)}</p>
					<button
						type="button"
						onClick={() => {
							void load();
						}}
						className="rounded-md bg-portal-accent hover:bg-portal-accent-hover text-white font-medium px-4 py-2 transition-colors"
					>
						{t('Try again')}
					</button>
				</div>
			</PaymentLinkShell>
		);
	}

	if (state == null || link == null) {
		return (
			<PaymentLinkShell>
				<div className="flex items-center justify-center py-16">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-portal-accent" />
				</div>
			</PaymentLinkShell>
		);
	}

	if (state !== 'active') {
		return (
			<PaymentLinkShell>
				<PaymentLinkStatePanel
					state={state}
					linkType={link?.linkType ?? undefined}
				/>
			</PaymentLinkShell>
		);
	}

	const isStatement = link.linkType === 'statement';
	const heading = isStatement
		? t('Pay your balance')
		: `${t('Pay invoice')} ${t('#')}${t(link.invoices[0]?.number ?? '')}`;

	return (
		<PaymentLinkShell>
			<div className="space-y-6">
				<section className="bg-brand-surface border border-brand-border rounded-xl p-5 sm:p-6 space-y-4">
					<div>
						<h1 className="text-2xl font-semibold text-default">{heading}</h1>
						<p className="text-sm text-brand-muted mt-1">
							{t(link.clientName ?? '')}
							{isStatement && link.statementNumber != null
								? ` · ${t('Statement')} ${t(link.statementNumber)}${
										link.statementDate != null
											? ` ${t('of')} ${t(formatDateSimple(link.statementDate))}`
											: ''
									}`
								: ''}
						</p>
					</div>

					{isStatement ? (
						<OpenInvoicePicker
							invoices={link.invoices.map((invoice) => ({
								id: invoice.invoiceId,
								invoiceNumber: invoice.number,
								date: invoice.date,
								dueDate: invoice.dueDate,
								outstandingBalance: invoice.balance,
							}))}
							lines={lines}
							onLinesChange={setLines}
							lineErrors={lineErrors}
							renderRowAction={(invoice) => (
								<button
									type="button"
									onClick={() => {
										void downloadInvoice(invoice.id, invoice.invoiceNumber);
									}}
									disabled={downloadingId === invoice.id}
									className="text-sm text-portal-accent hover:underline disabled:opacity-60 flex items-center gap-1"
								>
									<FontAwesomeIcon icon={faFilePdf} />
									{downloadingId === invoice.id ? t('Preparing...') : t('View')}
								</button>
							)}
						/>
					) : (
						<ul className="divide-y divide-brand-border border-y border-brand-border">
							{link.invoices.map((invoice) => (
								<li
									key={invoice.invoiceId}
									className="py-3 flex items-center justify-between gap-3"
								>
									<div className="min-w-0">
										<p className="text-sm font-medium text-default">
											{`${t('Invoice')} ${t('#')}${t(invoice.number)}`}
										</p>
										<p className="text-xs text-brand-muted">
											{invoice.dueDate != null
												? `${t('Due')} ${t(formatDateSimple(invoice.dueDate))}`
												: invoice.date != null
													? `${t('Dated')} ${t(formatDateSimple(invoice.date))}`
													: t('-')}
										</p>
									</div>
									<div className="flex items-center gap-3 shrink-0">
										<span className="text-sm font-semibold text-default">
											{t(formatCurrency(invoice.balance) ?? '')}
										</span>
										<button
											type="button"
											onClick={() => {
												void downloadInvoice(invoice.invoiceId, invoice.number);
											}}
											disabled={downloadingId === invoice.invoiceId}
											className="text-sm text-portal-accent hover:underline disabled:opacity-60 flex items-center gap-1"
										>
											<FontAwesomeIcon icon={faFilePdf} />
											{downloadingId === invoice.invoiceId
												? t('Preparing...')
												: t('View')}
										</button>
									</div>
								</li>
							))}
						</ul>
					)}

					<div className="flex items-center justify-between">
						<span className="text-sm text-brand-muted">{t('Balance due')}</span>
						<span className="text-lg font-semibold text-default">
							{t(formatCurrency(openBalance) ?? '')}
						</span>
					</div>
					{isStatement ? (
						<div className="flex items-center justify-between">
							<span className="text-sm text-default font-medium">
								{t('Total payment')}
							</span>
							<span className="text-lg font-semibold text-default">
								{t(formatCurrency(isAmountValid ? amount : 0) ?? '')}
							</span>
						</div>
					) : null}
				</section>

				<section className="bg-brand-surface border border-brand-border rounded-xl p-5 sm:p-6 space-y-5">
					{isStatement ? null : (
						<div>
							<label
								htmlFor="payment-link-amount"
								className="block text-sm font-medium text-default mb-1"
							>
								{t('Amount to pay')}
							</label>
							<div id="payment-link-amount">
								<CurrencyInput
									value={amountText}
									onChange={setAmountText}
									size="lg"
								/>
							</div>
							<p className="text-xs text-brand-muted mt-1">
								{isAmountValid
									? t('You can lower the amount to pay part of the balance.')
									: `${t('Enter an amount greater than zero and no more than')} ${t(formatCurrency(openBalance) ?? '')}.`}
							</p>
						</div>
					)}

					{api != null ? (
						<PortalAddPaymentMethod
							api={api}
							onSuccess={() => undefined}
							supportedPaymentMethods={link.paymentTypes}
							preSelectedPaymentType={initialPaymentType}
							onPaymentTypeChange={setPaymentType}
							submitButtonLabel={
								isAmountValid
									? `${t('Pay')} ${t(formatCurrency(total) ?? '')}`
									: t('Pay')
							}
							footerContent={
								<div className="space-y-4">
									{isAmountValid ? (
										<PaymentSummary
											lines={[
												{ label: t('Payment'), amount },
												{
													label: isCardFee
														? t('Card processing fee')
														: t('Processing fee'),
													amount: feeAmount,
													hideWhenZero: true,
													hint: isCardFee
														? t(
																'Applies to credit cards only. Debit and prepaid cards are not charged a fee.',
															)
														: undefined,
												},
											]}
											total={total}
										/>
									) : null}
									<div>
										<label className="flex items-center gap-2 cursor-pointer">
											<input
												type="checkbox"
												checked={saveForFuture}
												onChange={(event) => {
													setSaveForFuture(event.target.checked);
												}}
											/>
											<span className="text-sm font-medium text-default">
												{t('Save this payment method for future payments')}
											</span>
										</label>
										<p className="ml-6 text-xs mt-1 text-brand-muted">
											{t(
												'When checked, it is saved to your account so it can be used for future payments. Otherwise it is used for this payment only.',
											)}
										</p>
									</div>
									{/* Turnstile draws here only when it needs the visitor to interact. */}
									<div ref={turnstile.containerRef} />
									{turnstile.failed ? (
										<p className="text-sm text-danger">
											{t(
												'The security check could not load. Please refresh the page and try again.',
											)}
										</p>
									) : null}
								</div>
							}
						/>
					) : null}
				</section>

				<div className="flex justify-center">
					<PaymentLinkSupportLine />
				</div>
			</div>
		</PaymentLinkShell>
	);
}
